import { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { publishToWeb, estimatePublishSize } from './publishOrchestrator.ts';
import { listExports, deleteExport } from '@/api/publishApi.ts';
import type { PublishProgress } from './publishOrchestrator.ts';
import type { PublishedExport } from '@/api/publishApi.ts';
import styles from './PublishDialog.module.css';

interface Props {
  open: boolean;
  onClose: () => void;
}

type Mode = 'options' | 'publishing' | 'done' | 'error' | 'manage';

function formatMB(mb: number): string {
  if (mb < 0.01) return '< 0.01 MB';
  return `${mb.toFixed(2)} MB`;
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

const STAGE_WEIGHTS: Record<string, number> = {
  preparing: 0.05,
  'uploading-images': 0.35,
  'uploading-audio': 0.25,
  'uploading-video': 0.2,
  building: 0.05,
  deploying: 0.1,
  done: 1,
};

function computeOverallProgress(progress: PublishProgress): number {
  const stages = Object.keys(STAGE_WEIGHTS);
  let cumulative = 0;
  for (const stage of stages) {
    if (stage === progress.stage) {
      const stageProgress = progress.total > 0 ? progress.current / progress.total : 0;
      return cumulative + STAGE_WEIGHTS[stage] * stageProgress;
    }
    cumulative += STAGE_WEIGHTS[stage];
  }
  return cumulative;
}

/** Standalone content (no backdrop) — used by ExportHubDialog */
export function PublishDialogContent({ onClose }: { onClose: () => void }) {
  const [mode, setMode] = useState<Mode>('options');
  const [includeVideo, setIncludeVideo] = useState(false);
  const [compressImages, setCompressImages] = useState(true);
  const [sizes, setSizes] = useState<{ thumbnailsMB: number; audioMB: number; videoMB: number; totalMB: number } | null>(null);
  const [progress, setProgress] = useState<PublishProgress | null>(null);
  const [deploymentUrl, setDeploymentUrl] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [copyLabel, setCopyLabel] = useState<'copy' | 'copied'>('copy');
  const [exports, setExports] = useState<PublishedExport[]>([]);
  const [loadingExports, setLoadingExports] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const urlInputRef = useRef<HTMLInputElement>(null);
  const { t } = useTranslation('export');

  // Estimate sizes when options change
  useEffect(() => {
    if (mode !== 'options') return;
    estimatePublishSize(includeVideo, compressImages).then(setSizes);
  }, [mode, includeVideo, compressImages]);

  const handlePublish = async () => {
    setMode('publishing');
    setProgress({ stage: 'preparing', current: 0, total: 1, message: t('publishProgressStarting') });

    try {
      const result = await publishToWeb({
        includeVideo,
        compressImages,
        onProgress: setProgress,
      });
      setDeploymentUrl(result.deploymentUrl);
      setMode('done');
    } catch (err) {
      console.error('Publish failed:', err);
      setErrorMessage((err as Error).message || 'Unknown error');
      setMode('error');
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(deploymentUrl);
      setCopyLabel('copied');
      setTimeout(() => setCopyLabel('copy'), 2000);
    } catch {
      urlInputRef.current?.select();
    }
  };

  const handleManage = async () => {
    setMode('manage');
    setLoadingExports(true);
    try {
      const list = await listExports();
      setExports(list);
    } catch (err) {
      console.error('Failed to load exports:', err);
      setExports([]);
    } finally {
      setLoadingExports(false);
    }
  };

  const handleDelete = async (exportId: string) => {
    if (!confirm(t('manageDeleteConfirm'))) return;
    setDeletingId(exportId);
    try {
      await deleteExport(exportId);
      setExports(prev => prev.filter(e => e.export_id !== exportId));
    } catch (err) {
      console.error('Delete failed:', err);
    } finally {
      setDeletingId(null);
    }
  };

  const overallProgress = progress ? computeOverallProgress(progress) : 0;

  return (
    <>
      {/* OPTIONS MODE */}
      {mode === 'options' && (
        <>
          {sizes && (
            <table className={styles.sizeTable}>
              <tbody>
                <tr>
                  <td>{t('sizeLabelThumbnails')}</td>
                  <td>{formatMB(sizes.thumbnailsMB)}</td>
                </tr>
                <tr>
                  <td>{t('sizeLabelAudio')}</td>
                  <td>{formatMB(sizes.audioMB)}</td>
                </tr>
                {sizes.videoMB > 0 && includeVideo && (
                  <tr>
                    <td>{t('sizeLabelVideo')}</td>
                    <td>{formatMB(sizes.videoMB)}</td>
                  </tr>
                )}
                <tr className={styles.total}>
                  <td>{t('sizeLabelUploadSize')}</td>
                  <td>{formatMB(sizes.totalMB)}</td>
                </tr>
              </tbody>
            </table>
          )}

          <label className={styles.checkRow}>
            <input
              type="checkbox"
              checked={compressImages}
              onChange={e => setCompressImages(e.target.checked)}
            />
            {t('checkboxCompressThumbnails')}
            <span className={styles.sizeHint}>{t('compressHint')}</span>
          </label>

          <label className={styles.checkRow}>
            <input
              type="checkbox"
              checked={includeVideo}
              onChange={e => setIncludeVideo(e.target.checked)}
            />
            {t('checkboxIncludeVideoShots')}
            {sizes && sizes.videoMB > 0 && (
              <span className={styles.sizeHint}>+{formatMB(sizes.videoMB)}</span>
            )}
          </label>

          <div className={styles.actions}>
            <button className={styles.manageLink} onClick={handleManage}>
              {t('manageExportsLink')}
            </button>
            <button className={styles.cancelBtn} onClick={onClose}>
              {t('cancelButton')}
            </button>
            <button className={styles.publishBtn} onClick={handlePublish}>
              {t('publishButton')}
            </button>
          </div>
        </>
      )}

      {/* PUBLISHING MODE */}
      {mode === 'publishing' && (
        <div className={styles.progressSection}>
          <div className={styles.progressMessage}>{progress?.message}</div>
          <div className={styles.progressBarOuter}>
            <div
              className={styles.progressBarInner}
              style={{ width: `${Math.round(overallProgress * 100)}%` }}
            />
          </div>
        </div>
      )}

      {/* DONE MODE */}
      {mode === 'done' && (
        <>
          <div className={styles.doneSection}>
            <div className={styles.doneMessage}>{t('doneMessage')}</div>
            <div className={styles.urlRow}>
              <input
                ref={urlInputRef}
                className={styles.urlInput}
                value={deploymentUrl}
                readOnly
                onFocus={e => e.target.select()}
              />
              <button className={styles.copyBtn} onClick={handleCopy}>
                {copyLabel === 'copied' ? t('copiedButton') : t('copyButton')}
              </button>
            </div>
            <a
              className={styles.openLink}
              href={deploymentUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t('openInNewTab')}
            </a>
          </div>
          <div className={styles.actions}>
            <button className={styles.cancelBtn} onClick={onClose}>
              {t('closeButton')}
            </button>
          </div>
        </>
      )}

      {/* ERROR MODE */}
      {mode === 'error' && (
        <>
          <div className={styles.errorMessage}>{errorMessage}</div>
          <div className={styles.actions}>
            <button className={styles.cancelBtn} onClick={onClose}>
              {t('cancelButton')}
            </button>
            <button className={styles.publishBtn} onClick={handlePublish}>
              {t('tryAgainButton')}
            </button>
          </div>
        </>
      )}

      {/* MANAGE MODE */}
      {mode === 'manage' && (
        <>
          <button className={styles.backBtn} onClick={() => setMode('options')}>
            {t('manageBackButton')}
          </button>
          <div className={styles.exportList}>
            {loadingExports && (
              <div className={styles.emptyList}>{t('manageLoading')}</div>
            )}
            {!loadingExports && exports.length === 0 && (
              <div className={styles.emptyList}>{t('manageNoExports')}</div>
            )}
            {!loadingExports && exports.map(exp => (
              <div key={exp.id} className={styles.exportItem}>
                <div className={styles.exportItemInfo}>
                  <div className={styles.exportItemTitle}>{exp.title}</div>
                  <div className={styles.exportItemMeta}>
                    {formatDate(exp.created_at)}
                    {' \u2022 '}
                    {exp.scene_count === 1 ? t('manageSceneCount', { count: exp.scene_count }) : t('manageSceneCountPlural', { count: exp.scene_count })},
                    {' '}{exp.shot_count === 1 ? t('manageShotCount', { count: exp.shot_count }) : t('manageShotCountPlural', { count: exp.shot_count })}
                  </div>
                  <a
                    className={styles.exportItemUrl}
                    href={exp.deployment_url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {exp.deployment_url}
                  </a>
                </div>
                <button
                  className={styles.deleteBtn}
                  onClick={() => handleDelete(exp.export_id)}
                  disabled={deletingId === exp.export_id}
                  title={t('manageDeleteTooltip')}
                >
                  {deletingId === exp.export_id ? '...' : '\u2715'}
                </button>
              </div>
            ))}
          </div>
          <div className={styles.actions}>
            <button className={styles.cancelBtn} onClick={onClose}>
              {t('closeButton')}
            </button>
          </div>
        </>
      )}
    </>
  );
}

/** Standalone dialog with backdrop — backward compatible */
export function PublishDialog({ open, onClose }: Props) {
  const { t } = useTranslation('export');
  if (!open) return null;

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.dialog} onClick={e => e.stopPropagation()}>
        <div className={styles.title}>{t('publishDialogTitle')}</div>
        <PublishDialogContent onClose={onClose} />
      </div>
    </div>
  );
}
